<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="deviceSelectModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="deviceSelectModalTitle">{{ title }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>

        <div class="modal-body">
          <!-- Altgeräte gibt es nicht als Masterartikel: dann ersetzt eine händische
               Bezeichnung Master, Farbe und Speichergröße. -->
          <div class="form-check device-legacy-toggle">
            <input id="device-legacy-toggle" v-model="isLegacy" class="form-check-input" type="checkbox" />
            <label class="form-check-label" for="device-legacy-toggle">Kein Master (Altgerät)</label>
          </div>

          <template v-if="isLegacy">
            <label for="device-legacy-description" class="form-label">Bezeichnung des Geräts</label>
            <input
              id="device-legacy-description"
              ref="legacyInput"
              v-model="alternativeText"
              type="text"
              class="form-control"
              maxlength="255"
              placeholder="z.B. iPhone 6s 64GB Gold"
              autocomplete="off"
              @keydown.enter.prevent="onConfirm"
            />
            <div class="form-text">
              Steht überall dort, wo sonst Master, Farbe und Speicher stehen — auch auf Lieferschein und Etikett.
            </div>
          </template>

          <template v-else>
          <p class="text-muted device-select-hint">{{ hint }}</p>

          <input
            ref="filterInput"
            v-model="filter"
            type="text"
            class="form-control device-select-filter"
            placeholder="Hersteller oder Bezeichnung…"
            autocomplete="off"
            @keydown.down.prevent="moveCursor(1)"
            @keydown.up.prevent="moveCursor(-1)"
            @keydown.enter.prevent="onConfirm"
          />

          <div v-if="loading" class="device-select-state">Lade…</div>
          <div v-else-if="error" class="device-select-state device-select-error">{{ error }}</div>
          <div v-else-if="devices.length === 0" class="device-select-state">
            Es ist kein Masterartikel für Reparatur freigegeben.
            Der Haken wird unter Stammdaten → Masterartikel gesetzt.
          </div>
          <div v-else-if="filtered.length === 0" class="device-select-state">Keine Treffer.</div>
          <ul v-else ref="listEl" class="device-select-list">
            <li
              v-for="(d, idx) in filtered"
              :key="d.id"
              :class="{ active: idx === cursor, selected: selectedDevice?.id === d.id }"
              @click="select(idx)"
              @dblclick="onConfirm"
            >
              <span class="device-select-vendor">{{ d.vendor.description || '—' }}</span>
              <span class="device-select-desc">{{ d.description || '—' }}</span>
              <span class="device-select-id">#{{ d.id }}</span>
            </li>
          </ul>
          </template>

          <!-- Schritt 2+3: Farbe und Speichergröße der konkreten Variante. Beides
               steht erst zur Wahl, wenn der Master feststeht. Beim Altgerät nur der Zustand. -->
          <div class="device-variant-row">
            <div v-if="!isLegacy" class="device-variant-field">
              <label for="device-variant-color" class="form-label">Farbe</label>
              <select
                id="device-variant-color"
                v-model="selectedColorId"
                class="form-select"
                :disabled="!selectedDevice || variantsLoading || colors.length === 0"
              >
                <option :value="null" disabled>— Bitte wählen —</option>
                <option v-for="c in colors" :key="c.id" :value="c.id">{{ c.description }}</option>
              </select>
            </div>

            <div v-if="!isLegacy" class="device-variant-field">
              <label for="device-variant-memory" class="form-label">Speichergröße</label>
              <select
                id="device-variant-memory"
                v-model="selectedMemorySize"
                class="form-select"
                :disabled="selectedColorId === null || memorySizes.length === 0"
              >
                <option :value="null" disabled>— Bitte wählen —</option>
                <option v-for="m in memorySizes" :key="m.memory_size" :value="m.memory_size">
                  {{ m.description }}
                </option>
              </select>
            </div>

            <div class="device-variant-field">
              <label for="device-variant-condition" class="form-label">Zustand</label>
              <select
                id="device-variant-condition"
                v-model="selectedConditionId"
                class="form-select"
                :disabled="(!selectedDevice && !isLegacy) || conditions.length === 0"
              >
                <option :value="null" disabled>— Bitte wählen —</option>
                <option v-for="c in conditions" :key="c.id" :value="c.id">{{ c.description }}</option>
              </select>
            </div>
          </div>

          <!-- Schritt 4: Wohin das Gerät später geht. Steht hier, obwohl das
               Lagergerät erst beim Ticketabschluss entsteht — dann wird daraus
               automatisch Versand und Etikett. Gibt es zum Master keine Rezeptur
               (Master × Servicepartner × Fehlerbild), wird nichts angezeigt. -->
          <div v-if="showPlannedShipment && shipmentOptions.length > 0" class="device-variant-row">
            <div class="device-variant-field">
              <label for="device-shipment-partner" class="form-label">Versand an Servicepartner</label>
              <select
                id="device-shipment-partner"
                v-model="selectedPartnerId"
                class="form-select"
                :disabled="plannedShipmentLocked"
              >
                <option :value="null">— kein Versand geplant —</option>
                <option v-for="p in shipmentOptions" :key="p.id" :value="p.id">{{ p.description }}</option>
              </select>
            </div>

            <div class="device-variant-field">
              <label for="device-shipment-pattern" class="form-label">Fehlerbild</label>
              <select
                id="device-shipment-pattern"
                v-model="selectedErrorPatternId"
                class="form-select"
                :disabled="plannedShipmentLocked || selectedPartnerId === null"
              >
                <option :value="null" disabled>
                  {{ selectedPartnerId === null ? 'Zuerst Servicepartner wählen' : '— Bitte wählen —' }}
                </option>
                <option v-for="pattern in shipmentPatterns" :key="pattern.id" :value="pattern.id">
                  {{ pattern.description }}{{ pattern.price_label ? ` · ${pattern.price_label}` : '' }}
                </option>
              </select>
            </div>

            <!-- Altgerät: keine Rezeptur, also auch kein Preis daraus — Pflichtfeld. -->
            <div v-if="isLegacy" class="device-variant-field">
              <label for="device-shipment-price" class="form-label">Preis netto (€)</label>
              <input
                id="device-shipment-price"
                v-model="plannedPriceText"
                type="text"
                inputmode="decimal"
                class="form-control"
                :class="{ 'is-invalid': plannedPriceInvalid }"
                placeholder="z.B. 45,00"
                :disabled="plannedShipmentLocked || selectedPartnerId === null"
              />
              <div v-if="plannedPriceInvalid" class="invalid-feedback">{{ MANUAL_PRICE_MESSAGE }}</div>
            </div>
          </div>

          <div v-if="showPlannedShipment && plannedShipmentLocked && shipmentOptions.length > 0" class="device-select-state device-variant-note">
            Das Ticket ist abgeschlossen — der geplante Versand lässt sich nicht mehr ändern.
          </div>

          <template v-if="!isLegacy">
          <div v-if="variantsLoading" class="device-select-state device-variant-note">Lade Varianten…</div>
          <div v-else-if="variantsError" class="device-select-state device-select-error device-variant-note">
            {{ variantsError }}
          </div>
          <div v-else-if="selectedDevice && colors.length === 0" class="device-select-state device-variant-note">
            Zu diesem Masterartikel sind keine Artikel angelegt — Farbe und Speichergröße lassen sich
            nicht wählen. Ohne beides kann das Ticket später nicht abgeschlossen werden.
          </div>
          <div v-else-if="providerFallback" class="device-select-state device-variant-note">
            Zu diesem Masterartikel gibt es keine Artikel mit Provider „Frei" — gelistet sind die
            Farben und Speichergrößen aller Provider.
          </div>
          </template>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Abbrechen</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canConfirm"
            @click="onConfirm"
          >OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, nextTick, watch } from 'vue';
import { Modal } from 'bootstrap';
import {
  listReceivedDeviceOptions,
  listDeviceVariants,
  listDeviceConditions,
  listMasterServicepartnerOptions,
  listLegacyServicepartnerOptions,
  DEFAULT_CONDITION_ID,
  type DeviceColorVariant,
  type DeviceConditionRef,
  type DeviceMemorySize,
  type ErrorPatternOption,
  type ReceivedDeviceResult,
  type ServicepartnerOption,
  ApiError,
} from '../services/tickets';
import { parseManualPrice } from '../services/stockDevices';

/** Gleicher Text wie R2RecipePrices::MANUAL_PRICE_MESSAGE auf dem Server. */
const MANUAL_PRICE_MESSAGE = 'Bitte einen Netto-Preis zwischen 0,01 € und 9.999,99 € angeben.';

/** Was der Aufrufer beim Öffnen schon gesetzt hat — wird vorausgewählt. */
interface DeviceSelectPreset {
  device_id:    number | null;
  color_id:     number | null;
  memory_size:  number | null;
  condition_id: number | null;
  planned_servicepartner_location_id: number | null;
  planned_error_pattern_id:           number | null;
  /** Abgeschlossenes Ticket: der geplante Versand ist nur noch Anzeige. */
  is_closed:    boolean;
  /** Altgerät: gesetzt statt device_id. */
  alternative_device_description?: string | null;
  /** Händischer Preis des geplanten Versands (nur Altgerät). */
  planned_price_net_eur?:           number | null;
}

/**
 * Womit die Maske auftritt. Sie wird an zwei Stellen benutzt: im Ticket für die
 * Wahl des eingegangenen Geräts (mit geplantem Versand) und im Gerätelager für die
 * Korrektur eines Lagergeräts (ohne — dort löst „Versand an Servicepartner" aus).
 */
export interface DeviceSelectOptions {
  title?:               string;
  hint?:                string;
  showPlannedShipment?: boolean;
}

const DEFAULT_TITLE = 'Eingegangenes Gerät wählen';
const DEFAULT_HINT  = 'Auswählbar sind nur Masterartikel, die in den Stammdaten für Reparatur '
  + 'freigegeben sind. Suche nach Hersteller oder Bezeichnung.';

interface DeviceSelectResult {
  /** null beim Altgerät — dann ist `alternative_device_description` gesetzt. */
  device:       ReceivedDeviceResult | null;
  alternative_device_description: string | null;
  planned_price_net_eur:          number | null;
  color_id:     number | null;
  memory_size:  number | null;
  condition_id: number | null;
  planned_servicepartner_location_id: number | null;
  planned_error_pattern_id:           number | null;
}

const modalEl     = useTemplateRef<HTMLDivElement>('modalEl');
const filterInput = useTemplateRef<HTMLInputElement>('filterInput');
const legacyInput = useTemplateRef<HTMLInputElement>('legacyInput');
const listEl      = useTemplateRef<HTMLUListElement>('listEl');
let   modal: Modal | null = null;
let   resolver: ((result: DeviceSelectResult | null) => void) | null = null;

const devices = ref<ReceivedDeviceResult[]>([]);
const loading = ref(false);
const error   = ref<string | null>(null);
const filter  = ref('');
const cursor  = ref(0);

const title               = ref(DEFAULT_TITLE);
const hint                = ref(DEFAULT_HINT);
const showPlannedShipment = ref(true);

const selectedDevice = ref<ReceivedDeviceResult | null>(null);
let   result: DeviceSelectResult | null = null;

const colors            = ref<DeviceColorVariant[]>([]);
const providerFallback  = ref(false);
const variantsLoading   = ref(false);
const variantsError     = ref<string | null>(null);
const selectedColorId   = ref<number | null>(null);
const selectedMemorySize = ref<number | null>(null);

const conditions          = ref<DeviceConditionRef[]>([]);
const selectedConditionId = ref<number | null>(null);

const shipmentOptions        = ref<ServicepartnerOption[]>([]);
const selectedPartnerId      = ref<number | null>(null);
const selectedErrorPatternId = ref<number | null>(null);
const plannedShipmentLocked  = ref(false);

const isLegacy         = ref(false);
const alternativeText  = ref('');
const plannedPriceText = ref('');
const plannedPrice     = computed<number | null>(() => parseManualPrice(plannedPriceText.value));
// Erst meckern, wenn etwas dasteht — ein leeres Feld zeigt nur der gesperrte OK-Knopf.
const plannedPriceInvalid = computed<boolean>(
  () => isLegacy.value && selectedPartnerId.value !== null
    && plannedPriceText.value.trim() !== '' && plannedPrice.value === null,
);

const shipmentPatterns = computed<ErrorPatternOption[]>(
  () => shipmentOptions.value.find(p => p.id === selectedPartnerId.value)?.error_patterns ?? [],
);

// Vorauswahl aus dem Ticket; wird beim ersten Variantenladen angewendet.
const EMPTY_PRESET: DeviceSelectPreset = {
  device_id:    null,
  color_id:     null,
  memory_size:  null,
  condition_id: null,
  planned_servicepartner_location_id: null,
  planned_error_pattern_id:           null,
  is_closed:    false,
};
let preset: DeviceSelectPreset = EMPTY_PRESET;

// Jeder Suchteil muss in „Hersteller Bezeichnung" vorkommen — „samsung a55" trifft.
const filtered = computed<ReceivedDeviceResult[]>(() => {
  const terms = filter.value.trim().toLowerCase().split(/\s+/).filter(t => t.length > 0);
  if (terms.length === 0) return devices.value;
  return devices.value.filter(d => {
    const haystack = `${d.vendor.description} ${d.description}`.toLowerCase();
    return terms.every(t => haystack.includes(t));
  });
});

const memorySizes = computed<DeviceMemorySize[]>(() => {
  const color = colors.value.find(c => c.id === selectedColorId.value);
  return color ? color.memory_sizes : [];
});

// Ohne Master geht gar nichts, und der Zustand gehört immer dazu. Gibt es zum
// Master Varianten, müssen zusätzlich Farbe UND Speichergröße stehen; gibt es
// keine, wird das Gerät ohne Variante gespeichert.
const canConfirm = computed<boolean>(() => {
  if (isLegacy.value) {
    if (alternativeText.value.trim() === '' || selectedConditionId.value === null) return false;
    if (!showPlannedShipment.value || selectedPartnerId.value === null) return true;
    return selectedErrorPatternId.value !== null && plannedPrice.value !== null;
  }
  if (!selectedDevice.value || variantsLoading.value) return false;
  if (selectedConditionId.value === null) return false;
  // Servicepartner ohne Fehlerbild ist ein halber Versand — dann lieber gar keiner.
  if (showPlannedShipment.value && selectedPartnerId.value !== null && selectedErrorPatternId.value === null) return false;
  if (colors.value.length === 0) return true;
  return selectedColorId.value !== null && selectedMemorySize.value !== null;
});

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
    modalEl.value.addEventListener('shown.bs.modal', onShown);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modalEl.value?.removeEventListener('shown.bs.modal', onShown);
  modal?.dispose();
  modal = null;
});

function open(current?: DeviceSelectPreset, options?: DeviceSelectOptions): Promise<DeviceSelectResult | null> {
  result                    = null;
  preset                    = current ?? EMPTY_PRESET;
  title.value               = options?.title ?? DEFAULT_TITLE;
  hint.value                = options?.hint ?? DEFAULT_HINT;
  showPlannedShipment.value = options?.showPlannedShipment ?? true;
  selectedDevice.value      = null;
  selectedColorId.value     = null;
  selectedMemorySize.value  = null;
  // Neues Ticket: „Gebraucht D" ist der Normalfall für ein Reparaturgerät.
  selectedConditionId.value = preset.condition_id ?? DEFAULT_CONDITION_ID;
  shipmentOptions.value        = [];
  selectedPartnerId.value      = null;
  selectedErrorPatternId.value = null;
  plannedShipmentLocked.value  = preset.is_closed;
  isLegacy.value               = (preset.alternative_device_description ?? null) !== null;
  alternativeText.value        = preset.alternative_device_description ?? '';
  plannedPriceText.value       = '';
  colors.value             = [];
  providerFallback.value   = false;
  variantsError.value      = null;
  filter.value             = '';
  cursor.value             = 0;
  void load();
  return new Promise<DeviceSelectResult | null>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

// Bei jedem Öffnen neu laden — Freigaben können zwischenzeitlich geändert worden sein.
async function load(): Promise<void> {
  loading.value = true;
  error.value   = null;
  try {
    if (conditions.value.length === 0) {
      // Stammdaten, ändern sich praktisch nie — einmal je Sitzung reicht.
      conditions.value = await listDeviceConditions();
    }
    devices.value = await listReceivedDeviceOptions();
    if (isLegacy.value) {
      if (showPlannedShipment.value) await loadLegacyShipmentOptions(true);
      return;
    }
    // Schon gesetztes Gerät vorauswählen, damit nur noch die Variante fehlt.
    if (preset.device_id !== null) {
      const known = devices.value.find(d => d.id === preset.device_id);
      if (known) {
        selectedDevice.value = known;
        cursor.value         = devices.value.indexOf(known);
      }
    }
  } catch (e) {
    error.value   = e instanceof ApiError ? `Fehler: ${e.message}` : 'Unbekannter Fehler.';
    devices.value = [];
  } finally {
    loading.value = false;
  }
}

function onShown(): void {
  void nextTick(() => (isLegacy.value ? legacyInput.value : filterInput.value)?.focus());
}

// Umschalten zwischen Master und Altgerät: die jeweils andere Auswahl gilt nicht mehr,
// ebenso der geplante Versand (Rezeptur vs. Stammdaten mit händischem Preis).
watch(isLegacy, legacy => {
  selectedDevice.value         = null;
  shipmentOptions.value        = [];
  selectedPartnerId.value      = null;
  selectedErrorPatternId.value = null;
  plannedPriceText.value       = '';
  if (legacy && showPlannedShipment.value) void loadLegacyShipmentOptions(false);
  void nextTick(() => (legacy ? legacyInput.value : filterInput.value)?.focus());
});

// Beim Tippen rückt die Auswahl auf den ersten Treffer — Enter bestätigt dann direkt.
// Ohne Suchbegriff bleibt bewusst nichts vorausgewählt, damit ein Fehlklick auf „OK"
// nicht das erstbeste Gerät ins Ticket schreibt.
watch(filtered, list => {
  cursor.value = 0;
  if (!isLegacy.value && filter.value.trim().length > 0) {
    selectedDevice.value = list[0] ?? null;
  }
});

// Master gewechselt → Varianten des neuen Masters laden.
watch(selectedDevice, device => {
  selectedColorId.value    = null;
  selectedMemorySize.value = null;
  colors.value             = [];
  providerFallback.value   = false;
  variantsError.value      = null;
  // Die Rezepturen hängen am Master — bei einem Wechsel gilt die alte Auswahl nicht mehr.
  shipmentOptions.value        = [];
  selectedPartnerId.value      = null;
  selectedErrorPatternId.value = null;
  if (device) {
    void loadVariants(device.id);
    // Ohne den Block braucht die Maske die Rezepturen nicht (Korrektur im Lager).
    if (showPlannedShipment.value) void loadShipmentOptions(device.id);
  }
});

// Partnerwechsel: Fehlerbild neu wählen, außer es passt weiterhin. Bewusst nicht
// blind auf null — die Vorauswahl aus dem Ticket setzt Partner und Fehlerbild
// nacheinander, und dieser Watcher läuft erst danach.
watch(selectedPartnerId, () => {
  if (!shipmentPatterns.value.some(p => p.id === selectedErrorPatternId.value)) {
    selectedErrorPatternId.value = null;
  }
});

// Farbe gewechselt → Speichergröße neu wählen, außer sie passt weiterhin.
watch(selectedColorId, () => {
  if (!memorySizes.value.some(m => m.memory_size === selectedMemorySize.value)) {
    selectedMemorySize.value = null;
  }
});

/**
 * Erlaubte Versandziele zum Master. Ein Fehler bleibt still: ohne Auswahl wird der
 * Bereich einfach nicht angezeigt, das darf die Gerätewahl nicht blockieren.
 */
async function loadShipmentOptions(masterId: number): Promise<void> {
  try {
    shipmentOptions.value = await listMasterServicepartnerOptions(masterId);
  } catch {
    shipmentOptions.value = [];
    return;
  }
  // Vorauswahl nur für den Master übernehmen, aus dem sie stammt.
  if (preset.device_id !== masterId || preset.planned_servicepartner_location_id === null) return;
  const partner = shipmentOptions.value.find(p => p.id === preset.planned_servicepartner_location_id);
  if (!partner) return;
  selectedPartnerId.value = partner.id;
  if (partner.error_patterns.some(p => p.id === preset.planned_error_pattern_id)) {
    selectedErrorPatternId.value = preset.planned_error_pattern_id;
  }
}

/**
 * Versandziele für ein Altgerät: alle aktiven Partner und Fehlerbilder. Wie beim
 * Master bleibt ein Fehler still. `applyPreset` übernimmt den Plan aus dem Ticket —
 * nur beim Öffnen, nicht nach einem Umschalten.
 */
async function loadLegacyShipmentOptions(applyPreset: boolean): Promise<void> {
  try {
    shipmentOptions.value = await listLegacyServicepartnerOptions();
  } catch {
    shipmentOptions.value = [];
    return;
  }
  if (!applyPreset || preset.planned_servicepartner_location_id === null) return;
  const partner = shipmentOptions.value.find(p => p.id === preset.planned_servicepartner_location_id);
  if (!partner) return;
  selectedPartnerId.value = partner.id;
  if (partner.error_patterns.some(p => p.id === preset.planned_error_pattern_id)) {
    selectedErrorPatternId.value = preset.planned_error_pattern_id;
  }
  if (preset.planned_price_net_eur !== null && preset.planned_price_net_eur !== undefined) {
    plannedPriceText.value = preset.planned_price_net_eur.toFixed(2).replace('.', ',');
  }
}

async function loadVariants(masterId: number): Promise<void> {
  variantsLoading.value = true;
  variantsError.value   = null;
  try {
    const variants         = await listDeviceVariants(masterId);
    colors.value           = variants.colors;
    providerFallback.value = variants.provider_fallback;

    // Vorauswahl nur für den Master übernehmen, aus dem sie stammt.
    if (preset.device_id === masterId && preset.color_id !== null) {
      const color = colors.value.find(c => c.id === preset.color_id);
      if (color) {
        selectedColorId.value = color.id;
        if (color.memory_sizes.some(m => m.memory_size === preset.memory_size)) {
          selectedMemorySize.value = preset.memory_size;
        }
      }
    }
  } catch (e) {
    variantsError.value = e instanceof ApiError
      ? `Varianten konnten nicht geladen werden: ${e.message}`
      : 'Varianten konnten nicht geladen werden.';
    colors.value = [];
  } finally {
    variantsLoading.value = false;
  }
}

function select(idx: number): void {
  const d = filtered.value[idx];
  if (!d) return;
  cursor.value = idx;
  if (selectedDevice.value?.id !== d.id) {
    selectedDevice.value = d;
  }
}

function moveCursor(delta: number): void {
  const count = filtered.value.length;
  if (count === 0) return;
  select((cursor.value + delta + count) % count);
  void nextTick(scrollCursorIntoView);
}

function scrollCursorIntoView(): void {
  listEl.value?.children[cursor.value]?.scrollIntoView({ block: 'nearest' });
}

function onConfirm(): void {
  if (!canConfirm.value) return;
  // Entweder beides oder nichts — ein halber Versand ist kein Versand.
  const plannedComplete = showPlannedShipment.value
    && selectedPartnerId.value !== null && selectedErrorPatternId.value !== null;
  if (isLegacy.value) {
    result = {
      device:       null,
      alternative_device_description: alternativeText.value.trim(),
      planned_price_net_eur:          plannedComplete ? plannedPrice.value : null,
      color_id:     null,
      memory_size:  null,
      condition_id: selectedConditionId.value,
      planned_servicepartner_location_id: plannedComplete ? selectedPartnerId.value : null,
      planned_error_pattern_id:           plannedComplete ? selectedErrorPatternId.value : null,
    };
    modal?.hide();
    return;
  }
  if (!selectedDevice.value) return;
  result = {
    device:       selectedDevice.value,
    alternative_device_description: null,
    planned_price_net_eur:          null,
    color_id:     colors.value.length === 0 ? null : selectedColorId.value,
    memory_size:  colors.value.length === 0 ? null : selectedMemorySize.value,
    condition_id: selectedConditionId.value,
    planned_servicepartner_location_id: plannedComplete ? selectedPartnerId.value : null,
    planned_error_pattern_id:           plannedComplete ? selectedErrorPatternId.value : null,
  };
  modal?.hide();
}

function onHidden(): void {
  resolver?.(result);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.device-legacy-toggle {
  margin-bottom: 0.6rem;
}

.device-select-hint {
  font-size: 0.9rem;
  margin-bottom: 0.6rem;
}

.device-select-filter {
  margin-bottom: 0.6rem;
}

.device-select-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  max-height: 320px;
  overflow-y: auto;
}

.device-select-list li {
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  cursor: pointer;
  font-size: 0.92rem;
  border-bottom: 1px solid #f1f1f4;
}

.device-select-list li:last-child {
  border-bottom: none;
}

.device-select-list li.active,
.device-select-list li:hover {
  background: #fff5e0;
}

.device-select-list li.selected {
  background: var(--accent-soft, rgba(247, 166, 0, 0.12));
  font-weight: 600;
}

.device-select-vendor {
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-select-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-select-id {
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.device-select-state {
  padding: 0.55rem 0.7rem;
  color: #6c757d;
  font-size: 0.92rem;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
}

.device-select-error {
  color: #b91c1c;
}

.device-variant-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 0.8rem;
}

.device-variant-field {
  flex: 1 1 180px;
}

.device-variant-note {
  margin-top: 0.6rem;
}

@media (max-width: 575.98px) {
  .device-variant-row {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
